import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import Lightbox from './Lightbox';

/**
 * iter129 — App-wide Lightbox provider.
 *
 * Wrap the app root once in <LightboxProvider>. Then anywhere in the tree call
 *   const { open } = useLightbox();
 *   open(images, startIndex);
 *
 * Or use the <Thumbnail> helper to wrap an <img>:
 *   <Thumbnail src={url} images={allUrls} index={i} />
 *
 * `images` accepts a list of strings or { url, filename, alt } objects.
 */
const LightboxContext = createContext({ open: () => {}, close: () => {} });

export const useLightbox = () => useContext(LightboxContext);

export const LightboxProvider = ({ children }) => {
  const [state, setState] = useState({ open: false, images: [], startIndex: 0 });

  const open = useCallback((images, startIndex = 0) => {
    if (!images || images.length === 0) return;
    setState({ open: true, images: Array.isArray(images) ? images : [images], startIndex });
  }, []);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  const value = useMemo(() => ({ open, close }), [open, close]);

  return (
    <LightboxContext.Provider value={value}>
      {children}
      <Lightbox open={state.open} images={state.images} startIndex={state.startIndex} onClose={close} />
    </LightboxContext.Provider>
  );
};

/**
 * Convenience wrapper around a clickable thumbnail. Renders an <img> that
 * launches the lightbox on click/tap.
 *
 *   <Thumbnail src={url} images={[url1, url2, url3]} index={1} className="..." />
 */
export const Thumbnail = ({ src, images, index = 0, alt = '', className = '', testId, onClick, ...rest }) => {
  const { open } = useLightbox();
  return (
    <img
      src={src}
      alt={alt}
      data-testid={testId}
      role="button"
      tabIndex={0}
      onClick={(e) => {
        if (typeof onClick === 'function') onClick(e);
        if (e.defaultPrevented) return;
        open(images && images.length ? images : [src], index);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open(images && images.length ? images : [src], index);
        }
      }}
      className={`cursor-zoom-in ${className}`}
      {...rest}
    />
  );
};

export default LightboxProvider;
